/* eslint-disable no-unsafe-optional-chaining */
/* eslint-disable unicorn/no-zero-fractions */
/* eslint-disable unicorn/numeric-separators-style */
import { useMediaQuery } from '@chakra-ui/react';
import { ReactGrid, Id, DropPosition } from '@silevis/reactgrid';
import { type RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { getCurrencySymbol, HEADER_HEIGHT_WITH_NAVIGATION } from 'projectConstants';
import {
  FetchPlangPageIndicatorTableData,
  UpdatePlangPageIndicatorTableData,
  filterEndDate,
  filterStartDate,
  getShowNonEditableColumns,
  getBlockDetails,
  getIndicatorTableData,
  getModelDetails,
  getmultiDimFiltersItems,
  updateIndicatorData,
} from 'redux/PlanPageSlice';
import {
  FetchBlockOutputs,
  getPlannerBlock,
  getPlannerModel,
  getBaseScenario as getBaseScenarioFromPlannerModeSlice,
} from 'redux/PlannerModeSlice';
import { getBaseScenario as getBaseScenarioFromScenarioSlice } from 'redux/ScenarioSlice';

import { StableHeaderRow } from './StableHeaderRow';
import { StableTextCellTemplate } from './StableTextCellTemplate';
import {
  buildColumnMetadata,
  buildProcessedRowsData,
  calculateColumnWidth,
  CellChange,
  Column,
  ColumnHeader,
  createTableRows,
  Dimension,
  extractYear,
  FilteredItem,
  FilterItem,
  getColumnId,
  getLocalCellValue,
  getReorderedDimensionIds,
  isHeaderNonEditable,
  monthNames,
  ProcessedFilterItem,
  reorderHeadersByDimensionOrder,
  Row,
  TableData,
  TableObject,
  UpdatePayload,
} from './MultiDimensionTableOpzV2.utils';

import './Table.scss';

interface Properties {
  refetchRequest: () => void;
  isPercentage: any;
  isBuilderMode?: boolean;
  isBuilderModeV1?: boolean;
}

interface GridProperties {
  rows: Row[];
  columns: Column[];
  onCellsChanged: (changes: any) => void;
  onColumnResized: (columnId: Id, width: number) => void;
  onColumnsReordered: (targetColumnId: Id, columnIds: Id[], dropPosition: DropPosition) => void;
  stickyLeftColumns: number;
  stickyTopRows: number;
}

const INPUT_PANEL_STORAGE_PREFIX = 'input-panel-opz-v2';

const getSessionJson = <T,>(key: string, fallback: T): T => {
  if (typeof window === 'undefined') return fallback;

  try {
    const value = window.sessionStorage.getItem(key);
    return value ? (JSON.parse(value) as T) : fallback;
  } catch {
    return fallback;
  }
};

const setSessionJson = (key: string, value: unknown) => {
  if (typeof window === 'undefined') return;

  try {
    window.sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Session persistence is a UX enhancement; grid editing should continue if storage is blocked.
  }
};

const useContainerWidth = (reference: RefObject<HTMLDivElement>) => {
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const element = reference.current;
    if (!element) return () => {};

    // Floor width so column math stays integer (subpixel widths distort ReactGrid panes).
    const updateWidth = () => setWidth(Math.floor(element.offsetWidth));
    updateWidth();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateWidth);
      return () => window.removeEventListener('resize', updateWidth);
    }

    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(element);
    return () => resizeObserver.disconnect();
  }, [reference]);

  return width;
};

const useTimeFilterIndices = (
  tableData: TableData | null,
  startDate: string,
  endDate: string,
  type: string,
  modelDetails?: any,
) => {
  return useMemo(() => {
    if (!tableData?.column_headers?.length) return null;

    const startDateValue =
      type === 'constant_by_year' ? extractYear(startDate, modelDetails) : startDate;
    const endDateValue = type === 'constant_by_year' ? extractYear(endDate, modelDetails) : endDate;

    const columnStartIndex = tableData.column_headers.findIndex(
      (cell) => String(cell.name) === String(startDateValue),
    );
    const columnEndIndex = tableData.column_headers.findIndex(
      (cell) => String(cell.name) === String(endDateValue),
    );

    if (columnStartIndex === -1 || columnEndIndex === -1) return null;

    return {
      columnStartIndex,
      columnEndIndex,
      activeDim: tableData.dimensions?.length || 0,
    };
  }, [tableData?.column_headers, startDate, endDate, type, modelDetails]);
};

const useGridProperties = (
  rows: Row[],
  columns: Column[],
  onCellsChanged: (changes: CellChange[]) => void,
  handleColumnResize: (columnId: Id, width: number) => void,
  handleColumnsReorder: (targetColumnId: Id, columnIds: Id[], dropPosition: DropPosition) => void,
  currentType: string,
  dimensionsLength: number,
): GridProperties => {
  return useMemo(
    () => ({
      rows,
      columns,
      onCellsChanged,
      onColumnResized: handleColumnResize,
      onColumnsReordered: handleColumnsReorder,
      stickyLeftColumns: currentType === 'constant' ? 0 : dimensionsLength,
      stickyTopRows: 1,
    }),
    [
      rows,
      columns,
      onCellsChanged,
      handleColumnResize,
      handleColumnsReorder,
      currentType,
      dimensionsLength,
    ],
  );
};

const useCellTemplates = () => {
  const headerRowTemplate = useCallback(() => new StableHeaderRow(), []);
  const textCellTemplate = useCallback(() => new StableTextCellTemplate(), []);

  return useMemo(
    () => ({
      header: headerRowTemplate(),
      text: textCellTemplate(),
    }),
    [headerRowTemplate, textCellTemplate],
  );
};

const useTableStyles = (
  isBuilderMode: boolean,
  isBuilderModeV1: boolean,
  isSmallDevice: boolean,
  isMobile: boolean,
  rowsLength: number,
  columnsLength: number,
) => {
  return useMemo(
    () => ({
      maxH: isBuilderModeV1
        ? '100%'
        : isBuilderMode
        ? `calc(95vh - ${HEADER_HEIGHT_WITH_NAVIGATION} - 35vh)`
        : isSmallDevice
        ? `calc(88vh - ${HEADER_HEIGHT_WITH_NAVIGATION} - 35vh)`
        : isMobile
        ? `calc(92vh - ${HEADER_HEIGHT_WITH_NAVIGATION} - 35vh)`
        : `calc(95vh - ${HEADER_HEIGHT_WITH_NAVIGATION} - 35vh)`,
      overflow: isBuilderModeV1 || rowsLength >= 2 ? 'auto' : 'none',
      paddingRight: isBuilderModeV1 ? '0' : rowsLength > 3 && columnsLength > 3 ? '0' : '1.3rem',
      height: undefined,
      flex: undefined,
      minHeight: isBuilderModeV1 ? 0 : undefined,
    }),
    [isBuilderMode, isBuilderModeV1, isSmallDevice, isMobile, rowsLength, columnsLength],
  );
};

const MultiDimensionTableOpzV2 = ({
  refetchRequest,
  isPercentage,
  isBuilderMode = false,
  isBuilderModeV1 = false,
}: Properties) => {
  const tableReference = useRef<HTMLDivElement>(null);
  const containerWidth = useContainerWidth(tableReference);
  const { blockId } = useParams();
  const dispatch: any = useDispatch();
  const indicatorTableData = useSelector(getIndicatorTableData);
  const baseScenarioFromPlannerMode = useSelector(getBaseScenarioFromPlannerModeSlice);
  const baseScenarioFromScenario = useSelector(getBaseScenarioFromScenarioSlice);
  const baseScenario = isBuilderMode ? baseScenarioFromPlannerMode : baseScenarioFromScenario;
  const dimensionList = useSelector(getBlockDetails);
  const timeDimension = dimensionList?.dimensions.find(
    (item: any) => item?.name?.toLowerCase() === 'time',
  );
  const modelData = useSelector(getModelDetails);
  const plannerModelData = useSelector(getPlannerModel);
  const modelDetails = modelData || plannerModelData;
  const plannerBlock = useSelector(getPlannerBlock);
  const currentType = indicatorTableData?.type;
  const [isMobile] = useMediaQuery('(max-height: 780px)');
  const [isSmallDevice] = useMediaQuery('(max-height: 680px)');
  const tableDataReference = useRef<TableData | null>(null);
  const [tableData, setTableData] = useState<TableData | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [customColumnWidths, setCustomColumnWidths] = useState<Record<string, number>>({});
  const [dimensionColumnOrder, setDimensionColumnOrder] = useState<string[]>([]);
  const filteredItems = useSelector(getmultiDimFiltersItems);
  const startDateFilter = useSelector(filterStartDate);
  const endDateFilter = useSelector(filterEndDate);
  const showNonEditableColumns = useSelector(getShowNonEditableColumns);

  const storageKey = useMemo(() => {
    const indicatorId = indicatorTableData?.indicator_id || 'no-indicator';
    const scenarioId = baseScenario?.id || 'no-scenario';
    const tableType = currentType || 'no-type';
    const dimensionIds =
      indicatorTableData?.dimensions?.map((dimension: { id: string }) => dimension.id).join('-') ||
      'none';
    return `${INPUT_PANEL_STORAGE_PREFIX}:${indicatorId}:${scenarioId}:${tableType}:${dimensionIds}`;
  }, [
    indicatorTableData?.indicator_id,
    indicatorTableData?.dimensions,
    baseScenario?.id,
    currentType,
  ]);

  useEffect(() => {
    setCustomColumnWidths(getSessionJson<Record<string, number>>(`${storageKey}:widths`, {}));
    setDimensionColumnOrder(getSessionJson<string[]>(`${storageKey}:dimension-order`, []));
  }, [storageKey]);

  const startDateObject = new Date(baseScenario?.start_date);
  startDateObject.setMonth(startDateObject.getMonth() - 1);
  const yearFormat = startDateObject.getFullYear();
  const monthFormat = `${monthNames[startDateObject.getMonth()]}-${String(yearFormat).slice(-2)}`;

  const targetDate = useCallback(() => {
    if (indicatorTableData?.type === 'raw') {
      return monthFormat;
    }
    return String(yearFormat);
  }, [indicatorTableData?.type, monthFormat, yearFormat]);

  const timeFilterIndices = useTimeFilterIndices(
    indicatorTableData,
    startDateFilter,
    endDateFilter,
    currentType,
    modelDetails,
  );

  const applyTimeFilter = useCallback(
    (data?: TableData) => {
      if (!data || !startDateFilter || !endDateFilter) return data;
      if (!timeFilterIndices) return data;

      const { columnStartIndex, columnEndIndex, activeDim } = timeFilterIndices;

      return {
        ...data,
        column_headers: [
          ...(activeDim > 0 ? data.column_headers.slice(0, activeDim) : []),
          ...data.column_headers.slice(columnStartIndex, columnEndIndex + 1),
        ],
        excel_data: data.excel_data.map((row) => {
          const newRow: Record<string, any> = {};
          if (activeDim > 0) {
            Object.entries(row)
              .slice(0, activeDim)
              .forEach(([key, value]) => {
                newRow[key] = value;
              });
          }
          Object.entries(row)
            .slice(columnStartIndex, columnEndIndex + 1)
            .forEach(([key, value]) => {
              newRow[key] = value;
            });
          return newRow;
        }),
      };
    },
    [timeFilterIndices, startDateFilter, endDateFilter],
  );

  const doesDimensionExist = useMemo(() => {
    return indicatorTableData?.dimensions?.find(
      (dim: any) => Number(filteredItems[0]?.dimId) === Number(dim.id),
    );
  }, [indicatorTableData?.dimensions, filteredItems]);

  const processedFilteredItems = useMemo(() => {
    if (!filteredItems?.length) return [];

    return filteredItems.map((itemData: FilteredItem) => ({
      dimensionId: `${itemData.dimId}`,
      filterItems: itemData.filterItems
        .filter((item: FilterItem) => item?.status && item?.search)
        .map((item: FilterItem) => item.name.trim().toLowerCase()),
    }));
  }, [filteredItems]);

  const transformedTableData = useMemo(() => {
    if (!indicatorTableData) return null;

    if (processedFilteredItems.length === 0 || !doesDimensionExist) {
      return startDateFilter && endDateFilter
        ? applyTimeFilter(indicatorTableData)
        : indicatorTableData;
    }

    const modifiedTable = {
      ...indicatorTableData,
      excel_data: indicatorTableData.excel_data.filter((object: TableObject) => {
        return processedFilteredItems.every(({ dimensionId, filterItems }: ProcessedFilterItem) => {
          const dimensionValue = object[dimensionId]?.value?.trim()?.toLowerCase();
          return dimensionValue && filterItems.includes(dimensionValue);
        });
      }),
    };

    return startDateFilter && endDateFilter ? applyTimeFilter(modifiedTable) : modifiedTable;
  }, [
    indicatorTableData,
    processedFilteredItems,
    doesDimensionExist,
    startDateFilter,
    endDateFilter,
    applyTimeFilter,
  ]);

  // Sync ref during render when server data changes so column/row memos don't use a stale
  // one-frame-behind snapshot (dimension toggles remount ReactGrid and crash on mismatch).
  // Seeded with null, not transformedTableData: seeding it with the value made
  // the comparison below false on the first render, so tableDataReference was
  // never populated. applyLocalCellChanges then bailed on its empty-ref guard
  // and an edited cell kept showing the old number even though the PUT had
  // succeeded — it only appeared after a reload.
  const previousTransformedTableData = useRef<TableData | null>(null);
  if (previousTransformedTableData.current !== transformedTableData) {
    previousTransformedTableData.current = transformedTableData;
    tableDataReference.current = transformedTableData;
  }

  useEffect(() => {
    setTableData(transformedTableData);
  }, [transformedTableData]);

  const orderedColumnHeaders = useMemo<ColumnHeader[]>(() => {
    const sourceTableData = tableDataReference.current || tableData;
    if (!sourceTableData?.column_headers?.length) return [];

    const activeDimensionIds = new Set(
      (indicatorTableData?.dimensions || []).map((dimension: { id: string }) =>
        String(dimension.id),
      ),
    );
    const headersFromApi = sourceTableData.column_headers.filter(
      (header: ColumnHeader) => !header.is_dimension || activeDimensionIds.has(String(header.id)),
    );

    return reorderHeadersByDimensionOrder(headersFromApi, dimensionColumnOrder);
  }, [tableData, transformedTableData, dimensionColumnOrder, indicatorTableData?.dimensions]);

  const gridStructureKey = useMemo(
    () =>
      [
        indicatorTableData?.indicator_id,
        currentType,
        orderedColumnHeaders.map((header) => `${header.id}:${header.name}`).join('|'),
        indicatorTableData?.dimensions?.map((dimension: { id: string }) => dimension.id).join('-'),
      ].join(':'),
    [
      indicatorTableData?.indicator_id,
      currentType,
      orderedColumnHeaders,
      indicatorTableData?.dimensions,
    ],
  );

  const visibleColumnHeaders = useMemo<ColumnHeader[]>(() => {
    if (orderedColumnHeaders.length === 0) return [];
    if (showNonEditableColumns) return orderedColumnHeaders;

    const scenarioDate = targetDate();
    return orderedColumnHeaders.filter(
      (header: ColumnHeader) => header.is_dimension || !isHeaderNonEditable(header, scenarioDate),
    );
  }, [orderedColumnHeaders, showNonEditableColumns, targetDate]);

  const columnMetadata = useMemo(
    () => buildColumnMetadata(visibleColumnHeaders, currentType, timeDimension?.id),
    [visibleColumnHeaders, currentType, timeDimension?.id],
  );

  const shouldFitConstantTableWidth = isBuilderModeV1 && currentType === 'constant';

  const columns = useMemo<Column[]>(() => {
    if (visibleColumnHeaders.length === 0) return [];
    if (!shouldFitConstantTableWidth && !containerWidth) return [];

    const columnWidth = calculateColumnWidth(
      visibleColumnHeaders.length,
      containerWidth || 0,
      currentType,
    );

    return visibleColumnHeaders.map((data: ColumnHeader): Column => {
      const columnId = getColumnId(data, currentType, timeDimension?.id);
      const rawWidth = shouldFitConstantTableWidth
        ? data.is_dimension
          ? 150
          : 120
        : customColumnWidths[columnId] || columnWidth;

      return {
        columnId,
        name: data.name,
        width: Math.floor(rawWidth),
        resizable: true,
        reorderable: !!data.is_dimension,
        timeDimId: timeDimension?.id,
      };
    });
  }, [
    visibleColumnHeaders,
    containerWidth,
    currentType,
    timeDimension?.id,
    customColumnWidths,
    shouldFitConstantTableWidth,
  ]);

  const handleColumnResize = useCallback(
    (columnId: Id, width: number) => {
      const columnIdString = String(columnId);
      const MIN_WIDTH = currentType === 'constant_by_year' ? 120 : 100;
      const MAX_WIDTH = currentType === 'constant_by_year' ? 800 : 600;
      const boundedWidth = Math.floor(Math.min(Math.max(width, MIN_WIDTH), MAX_WIDTH));

      setCustomColumnWidths((previousWidths: Record<string, number>) => {
        const nextWidths = {
          ...previousWidths,
          [columnIdString]: boundedWidth,
        };
        setSessionJson(`${storageKey}:widths`, nextWidths);
        return nextWidths;
      });
    },
    [currentType, storageKey],
  );

  const computedRows = useMemo<Row[]>(() => {
    const rowSource = tableDataReference.current || tableData;
    if (!rowSource?.excel_data?.length || visibleColumnHeaders.length === 0) return [];
    const currencySymbol = getCurrencySymbol(modelDetails?.reporting_currency);
    const date = targetDate();
    const processedData = buildProcessedRowsData(rowSource, orderedColumnHeaders);

    return createTableRows(processedData, visibleColumnHeaders, isPercentage, currencySymbol, date);
  }, [
    tableData,
    transformedTableData,
    visibleColumnHeaders,
    orderedColumnHeaders,
    isPercentage,
    targetDate,
    modelDetails?.reporting_currency,
  ]);

  useEffect(() => {
    setRows(computedRows);
  }, [computedRows]);

  // ReactGrid crashes if columns/rows cell counts diverge (e.g. after clearing dimensions:
  // columns update synchronously while `rows` state is still one frame behind).
  const gridRows = useMemo(() => {
    const columnCount = columns.length;
    if (!columnCount) return [];
    if (rows[0]?.cells?.length === columnCount) return rows;
    return computedRows[0]?.cells?.length === columnCount ? computedRows : [];
  }, [rows, columns.length, computedRows]);

  const processCellChanges = useCallback(
    (changes: CellChange[]) => {
      const organizedData: Record<string, Record<string, any>> = {};

      changes.forEach(({ rowId, columnId, newCell }) => {
        const value = getLocalCellValue(newCell.text, isPercentage, String(columnId));

        if (!organizedData[rowId]) {
          organizedData[rowId] = {};
        }
        organizedData[rowId][columnId] = value;
      });

      return Object.entries(organizedData).map(([rowId, rowData]) => ({
        ...rowData,
        rowId: Number(rowId),
      }));
    },
    [isPercentage],
  );

  const processDimensionData = useCallback(
    (result: Array<Record<string, any>>, tableRows: Row[]) => {
      return result.map((dataObject) => {
        const matchingRow = tableRows.find((row) => row.rowId === String(dataObject.rowId));
        if (matchingRow) {
          const dimensionData = matchingRow.cells
            .filter((cell) => cell.property === 'dimension')
            .reduce((accumulator, cell) => {
              if (cell.rowId) {
                accumulator[cell.rowId] = cell.text;
              }
              return accumulator;
            }, {} as Record<string, string>);

          const resultWithDimensions = { ...dataObject, ...dimensionData };
          delete resultWithDimensions.rowId;
          return resultWithDimensions;
        }
        const { rowId, ...rest } = dataObject;
        return rest;
      });
    },
    [],
  );

  const createUpdatePayload = useCallback(
    (
      data: Array<Record<string, any>>,
      type: string,
      dimensions: Array<{ id: string | number; name: string }>,
      scenarioId: string | number,
      indicatorId: string,
    ): UpdatePayload => ({
      indicator_id: indicatorId,
      request_body: {
        bulk_input: true,
        format_excel_data: 'true',
        dimensions,
        data_values: data,
        type,
        scenario_id: scenarioId,
      },
    }),
    [],
  );

  const applyLocalCellChanges = useCallback(
    (changes: CellChange[]) => {
      const previousTableData = tableDataReference.current;
      if (!previousTableData?.excel_data?.length) return;

      let hasDataChanges = false;
      const nextExcelData = [...previousTableData.excel_data];
      const displayValueByRowAndColumn = new Map<string, string>();

      changes.forEach(({ rowId, columnId, newCell }) => {
        const rowIndex = Number(rowId) - 1;
        if (!Number.isInteger(rowIndex) || rowIndex < 0 || rowIndex >= nextExcelData.length) {
          return;
        }

        const columnIdString = String(columnId);
        const columnKey = columnMetadata.localKeyByColumnId.get(columnIdString) || columnIdString;
        const nextValue = getLocalCellValue(newCell.text, isPercentage, columnKey);
        const row = nextExcelData[rowIndex];
        if (!row) return;

        if (row[columnKey] !== nextValue) {
          nextExcelData[rowIndex] = {
            ...row,
            [columnKey]: nextValue,
          };
          hasDataChanges = true;
        }

        displayValueByRowAndColumn.set(
          `${rowId}:${columnKey}`,
          newCell.text === '' ? '0' : newCell.text,
        );
      });

      if (hasDataChanges) {
        tableDataReference.current = {
          ...previousTableData,
          excel_data: nextExcelData,
        };
      }

      if (displayValueByRowAndColumn.size === 0) return;
      setRows((previousRows) => {
        let hasRowChanges = false;
        const nextRows = previousRows.map((row) => {
          let rowChanged = false;
          const nextCells = row.cells.map((cell) => {
            const cellColumnKey =
              cell.data?.name || (cell.columnId ? String(cell.columnId) : undefined);
            if (!cellColumnKey) return cell;

            const nextText = displayValueByRowAndColumn.get(`${row.rowId}:${cellColumnKey}`);
            if (nextText === undefined || cell.text === nextText) return cell;

            rowChanged = true;
            hasRowChanges = true;
            return {
              ...cell,
              text: nextText,
            };
          });

          return !rowChanged
            ? row
            : {
                ...row,
                cells: nextCells,
              };
        });

        return hasRowChanges ? nextRows : previousRows;
      });
    },
    [columnMetadata.localKeyByColumnId, isPercentage],
  );

  const fetchIndicatorsData = useCallback(async () => {
    if (!plannerBlock?.dimensions?.length) return;

    const dimensionIds = plannerBlock.dimensions
      .filter((item: Dimension) => item.name !== 'Time')
      .map((item: Dimension) => item.id)
      .join(', ');

    dispatch(
      FetchBlockOutputs({
        blockId,
        params: {
          dim_id: dimensionIds,
          indicator_filter: 'all',
          scenario_id: baseScenario?.id,
        },
      }),
    );
  }, [plannerBlock?.dimensions, blockId, baseScenario?.id, dispatch]);

  const updateCellData = useCallback(
    async (changes: CellChange[]) => {
      if (!changes?.length || !indicatorTableData) return;

      const result = processCellChanges(changes);
      const processedData = processDimensionData(result, rows);

      try {
        let payload: UpdatePayload;

        if (indicatorTableData.type === 'constant_by_year' || indicatorTableData.type === 'raw') {
          const dimId = timeDimension?.id;
          const response = processedData.flatMap((change) => {
            const dimKeys = Object.keys(change).filter((key) => key.startsWith(String(dimId)));
            return dimKeys.map((dimKey) => {
              const [, year] = dimKey.split(' ');
              const { [dimKey]: unusedValue, ...rest } = change;
              const rowPayload = {
                [String(dimId)]: year,
                value: change[dimKey],
                ...rest,
              };
              const newPayload: Record<string, any> = {};
              Object.keys(rowPayload).forEach((key) => {
                if (key.split(' ').length === 1) {
                  newPayload[key] = rowPayload[key];
                }
              });
              return newPayload;
            });
          });

          payload = createUpdatePayload(
            response,
            indicatorTableData.type,
            indicatorTableData.dimensions,
            baseScenario?.id,
            indicatorTableData.indicator_id,
          );
        } else {
          const cleanData = processedData.map(({ rowId, ...rest }) => rest);
          payload = createUpdatePayload(
            cleanData,
            indicatorTableData.type,
            indicatorTableData.dimensions,
            baseScenario?.id,
            indicatorTableData.indicator_id,
          );
        }

        await dispatch(
          UpdatePlangPageIndicatorTableData({ ...payload, skipStoreUpdate: true }),
        ).unwrap();
        applyLocalCellChanges(changes);

        // Refresh plan/chart data so Input Panel edits show up immediately.
        refetchRequest();
        if (plannerBlock?.dimensions?.length > 0) {
          await fetchIndicatorsData();
        }
      } catch {
        // Intentionally swallow; callers refetch on success path.
      }
    },
    [
      indicatorTableData,
      rows,
      timeDimension?.id,
      baseScenario?.id,
      plannerBlock?.dimensions,
      processCellChanges,
      processDimensionData,
      createUpdatePayload,
      applyLocalCellChanges,
      refetchRequest,
      fetchIndicatorsData,
      dispatch,
    ],
  );

  const handleColumnsReorder = useCallback(
    async (targetColumnId: Id, columnIds: Id[]) => {
      const currentIds = orderedColumnHeaders
        .filter((dim: ColumnHeader) => dim.is_dimension)
        .map((dim: ColumnHeader) => String(dim.id));
      const idsArray = getReorderedDimensionIds(currentIds, targetColumnId, columnIds);

      setDimensionColumnOrder(idsArray);
      setSessionJson(`${storageKey}:dimension-order`, idsArray);

      const payload = {
        indicator_id: indicatorTableData?.indicator_id,
        request_body: {
          dimension_ids: idsArray,
          scenario_id: baseScenario?.id,
        },
      };

      await dispatch(updateIndicatorData(payload));
      dispatch(
        FetchPlangPageIndicatorTableData({
          indicator_id: indicatorTableData?.indicator_id,
          parameters: {
            scenario_id: baseScenario?.id,
            format_excel_data: true,
          },
        }),
      );
    },
    [orderedColumnHeaders, storageKey, indicatorTableData, baseScenario?.id, dispatch],
  );

  const cellTemplates = useCellTemplates();
  const gridProperties = useGridProperties(
    gridRows,
    columns,
    updateCellData,
    handleColumnResize,
    handleColumnsReorder,
    currentType,
    visibleColumnHeaders.filter((header) => header.is_dimension).length,
  );
  const tableStyles = useTableStyles(
    Boolean(isBuilderMode),
    Boolean(isBuilderModeV1),
    Boolean(isSmallDevice),
    Boolean(isMobile),
    gridRows?.length || 0,
    columns?.length || 0,
  );
  const shouldFillAvailableHeight =
    isBuilderModeV1 && !shouldFitConstantTableWidth && (gridRows?.length || 0) > 6;

  // Virtual scrolling desyncs sticky panes on short viewports / weaker GPUs ("distorted" table).
  // Forecast already disables it for the same ReactGrid pattern; keep it for large tables only.
  const disableVirtualScrolling =
    Boolean(isSmallDevice) || Boolean(isMobile) || (gridRows?.length || 0) < 80;

  const wrapperStyle = isBuilderModeV1
    ? {
        width: shouldFitConstantTableWidth ? 'fit-content' : '100%',
        maxWidth: shouldFitConstantTableWidth ? '100%' : undefined,
        alignSelf: shouldFitConstantTableWidth ? ('flex-start' as const) : undefined,
        height: shouldFillAvailableHeight ? '100%' : 'auto',
        display: 'flex',
        flexDirection: 'column' as const,
        minHeight: 0,
        flex: shouldFillAvailableHeight ? 1 : undefined,
      }
    : { width: '100%' };

  return (
    <div
      style={wrapperStyle}
      className={shouldFitConstantTableWidth ? 'builder-mode-v1-fit-content' : undefined}
    >
      <div
        style={{
          width: shouldFitConstantTableWidth ? 'fit-content' : '100%',
          maxWidth: shouldFitConstantTableWidth ? '100%' : undefined,
          maxHeight: tableStyles.maxH,
          height: shouldFillAvailableHeight ? '100%' : 'auto',
          flex: shouldFillAvailableHeight ? 1 : undefined,
          minHeight: tableStyles.minHeight,
          overflow: tableStyles.overflow === 'none' ? 'visible' : tableStyles.overflow,
          paddingRight: tableStyles.paddingRight,
        }}
        ref={tableReference}
        className={`plan-revamp-md-table md-block-table-container sm-scroll${
          shouldFillAvailableHeight ? ' builder-mode-v1-fill-height' : ''
        }${shouldFitConstantTableWidth ? ' builder-mode-v1-fit-content' : ''}`}
      >
        {columns?.length > 0 && gridRows?.length > 0 && indicatorTableData && (
          <ReactGrid
            key={gridStructureKey}
            disableVirtualScrolling={disableVirtualScrolling}
            {...gridProperties}
            enableColumnSelection
            enableRangeSelection
            enableFillHandle
            customCellTemplates={cellTemplates}
          />
        )}
      </div>
    </div>
  );
};

export default MultiDimensionTableOpzV2;
